export const ELEMENT_STYLE_CONTENT_PLACEHOLDER = '{content}'

export type ParsedElementStyleWrapper = {
  hasWrapper: boolean
  cssText: string
  beforeHtml: string
  afterHtml: string
}

function readElementStyleRaw(style: unknown): string {
  if (style == null) return ''
  if (typeof style === 'string') return style.trim()
  if (typeof style === 'object') {
    const record = style as Record<string, unknown>
    const fromText = record._text ?? record._cdata
    if (fromText != null) return String(fromText).trim()
    if (typeof record._attributes === 'object' && record._attributes != null) {
      const attrStyle = (record._attributes as Record<string, unknown>).style
      if (attrStyle != null) return String(attrStyle).trim()
    }
  }
  return ''
}

export function readElementStyleFromConfig(element: unknown): string {
  if (element == null || typeof element !== 'object') return ''
  const record = element as Record<string, unknown>
  const fromAttr = record._attributes
  if (fromAttr && typeof fromAttr === 'object') {
    const attrStyle = (fromAttr as Record<string, unknown>).style
    if (attrStyle != null) return String(attrStyle).trim()
  }
  return readElementStyleRaw(record.style)
}

export function parseElementStyleWrapper(styleRaw: string): ParsedElementStyleWrapper {
  const raw = String(styleRaw ?? '').trim()
  const cached = parsedStyleCache.get(raw)
  if (cached) return cached
  if (!raw) {
    const empty: ParsedElementStyleWrapper = {
      hasWrapper: false,
      cssText: '',
      beforeHtml: '',
      afterHtml: '',
    }
    rememberParsedStyle(raw, empty)
    return empty
  }

  const slotMarkers = [ELEMENT_STYLE_CONTENT_PLACEHOLDER, '<slot></slot>', '<slot/>', '<slot />']
  for (const marker of slotMarkers) {
    const index = raw.indexOf(marker)
    if (index >= 0) {
      const beforeRaw = raw.slice(0, index)
      const afterRaw = raw.slice(index + marker.length)

      // Extract CSS from both before and after parts
      const beforeParts = splitMetadataStyleWrapperCss(beforeRaw)
      const afterParts = splitMetadataStyleWrapperCss(afterRaw)

      const parsed: ParsedElementStyleWrapper = {
        hasWrapper: true,
        cssText: [beforeParts.cssText, afterParts.cssText].filter(Boolean).join('\n'),
        beforeHtml: beforeParts.markupHtml,
        afterHtml: afterParts.markupHtml,
      }
      rememberParsedStyle(raw, parsed)
      return parsed
    }
  }

  // No marker found - treat entire content as wrapper
  const parts = splitMetadataStyleWrapperCss(raw)
  const parsed: ParsedElementStyleWrapper = {
    hasWrapper: true,
    cssText: parts.cssText,
    beforeHtml: parts.markupHtml,
    afterHtml: '',
  }
  rememberParsedStyle(raw, parsed)
  return parsed
}

const parsedStyleCache = new Map<string, ParsedElementStyleWrapper>()

function rememberParsedStyle(raw: string, parsed: ParsedElementStyleWrapper) {
  if (parsedStyleCache.size > 40) parsedStyleCache.clear()
  parsedStyleCache.set(raw, parsed)
}

export function resolveElementStyleWrapper(element: unknown): ParsedElementStyleWrapper {
  return parseElementStyleWrapper(readElementStyleFromConfig(element))
}

export function hasElementCustomStyle(element: unknown): boolean {
  return readElementStyleFromConfig(element).length > 0
}

const METADATA_STYLE_BLOCK_RE = /<style\b[^>]*>([\s\S]*?)<\/style>/gi

/** Отделяет CSS из `<style>` в metadata wrapper — v-html не применяет вложенные style надёжно. */
export function splitMetadataStyleWrapperCss(html: string): {
  cssText: string
  markupHtml: string
} {
  const raw = String(html ?? '')
  if (!raw.trim()) {
    return { cssText: '', markupHtml: '' }
  }

  const cssChunks: string[] = []
  let match: RegExpExecArray | null
  METADATA_STYLE_BLOCK_RE.lastIndex = 0
  while ((match = METADATA_STYLE_BLOCK_RE.exec(raw)) !== null) {
    const chunk = match[1]?.trim()
    if (chunk) cssChunks.push(chunk)
  }

  const markupHtml = raw.replace(METADATA_STYLE_BLOCK_RE, '').trim()
  return {
    cssText: cssChunks.join('\n'),
    markupHtml,
  }
}
